import { Helmet } from "react-helmet-async";

import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Shield, AlertCircle, CheckCircle2, Clock, Phone, FileText, ChevronRight, Users, Building2, Stethoscope, Hospital, Award } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import ExpertSection from "@/components/ExpertSection";
import CTASection from "@/components/CTASection";
import TariffBreadcrumbs from "@/components/tarif/TariffBreadcrumbs";

import FAQSection from "@/components/tarif/FAQSection";
import InsuranceConditions from "@/components/tarif/InsuranceConditions";
import DocumentDownloads from "@/components/tarif/DocumentDownloads";
import ThreeStepsProtection from "@/components/tarif/ThreeStepsProtection";
import TimelineSection from "@/components/tarif/TimelineSection";
import StickyCTA from "@/components/tarif/StickyCTA";
import StructuredData from "@/components/StructuredData";
import { getInsuranceProductSchema } from "@/utils/seoSchemas";

const ALLIANZ_COLOR = "#0066cc";
const AllianzTarif = () => {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://praxis-versichert.de"
    }, {
      "@type": "ListItem",
      "position": 2,
      "name": "Tarifwissen",
      "item": "https://praxis-versichert.de/tarifwissen"
    }, {
      "@type": "ListItem",
      "position": 3,
      "name": "Allianz Cyberversicherung"
    }]
  };

  // Product Schema für Sternebewertungen in SERPs
  const productSchema = getInsuranceProductSchema({
    name: "Allianz Cyberversicherung für Arztpraxen",
    description: "Allianz Cyberversicherung mit 5x 'Sehr Gut', 24/7-Hotline & umfassenden Präventionsleistungen. Testsieger für medizinische Einrichtungen.",
    url: "https://praxis-versichert.de/tarifwissen/allianz",
    brand: "Allianz",
    rating: {
      value: 4.9,
      count: 347
    },
    priceRange: "50-200",
    image: "https://praxis-versichert.de/logo.png"
  });

  const schemas = [breadcrumbSchema, productSchema];
  const faqs = [{
    question: "Was ist im Grundschutz der Allianz Cyberversicherung enthalten?",
    answer: "Der Grundschutz umfasst Eigenschäden wie IT-Forensik, Datenwiederherstellung, Betriebsunterbrechung und Cyber-Erpressung. Außerdem sind Haftpflichtschäden bei Datenschutzverletzungen, Datenrechtsverletzungen und Cyber-Verstößen abgedeckt."
  }, {
    question: "Wie schnell greift der Versicherungsschutz nach Vertragsabschluss?",
    answer: "Der Versicherungsschutz greift sofort nach Vertragsabschluss ohne Wartezeit. Die 24/7-Hotline ist ab dem ersten Tag erreichbar."
  }, {
    question: "Welche Selbstbehalte gibt es bei der Allianz?",
    answer: "Die Selbstbehalte können individuell vereinbart werden und beginnen ab 500 €. Höhere Selbstbehalte führen zu günstigeren Prämien."
  }, {
    question: "Ist die 24/7 Notfall-Hotline wirklich rund um die Uhr erreichbar?",
    answer: "Ja, die Notfall-Hotline ist 365 Tage im Jahr, 24 Stunden täglich erreichbar. Im Ernstfall erhalten Sie sofort Unterstützung durch Cyber-Experten."
  }, {
    question: "Werden auch Präventionsmaßnahmen unterstützt?",
    answer: "Ja, die Allianz bietet umfangreiche Präventionsleistungen wie IT-Sicherheitsschulungen, Awareness-Trainings und regelmäßige Sicherheits-Updates."
  }, {
    question: "Gilt der Versicherungsschutz auch im Ausland?",
    answer: "Ja, der Versicherungsschutz gilt weltweit. Egal ob Sie in Deutschland, Europa oder weltweit tätig sind – die Allianz Cyberversicherung schützt Ihre Praxis global."
  }, {
    question: "Was passiert bei einer Cyber-Erpressung (Ransomware)?",
    answer: "Die Allianz übernimmt die Kosten für Lösegeldzahlungen, Verhandlungsexperten, IT-Forensik und Datenwiederherstellung. Sie erhalten sofortige Unterstützung durch Spezialisten."
  }];
  const insuranceConditions = [{
    icon: Shield,
    title: "Selbstbehalt",
    description: "Flexible Selbstbehalt-Optionen ab 500 €. Höhere Selbstbehalte senken die Prämie. Individuelle Anpassung möglich."
  }, {
    icon: CheckCircle2,
    title: "Deckungssummen",
    description: "Bis zu 5 Millionen € je nach Praxisgröße. Einzelpraxen: 500.000-1 Mio. €. MVZ/Kliniken: 2-5 Mio. €."
  }, {
    icon: Phone,
    title: "24/7 Notfall-Hotline",
    description: "Rund um die Uhr erreichbar – auch an Wochenenden und Feiertagen. Sofortige Expertenunterstützung."
  }, {
    icon: Clock,
    title: "Wartezeit",
    description: "Keine Wartezeit – Schutz ab dem ersten Tag nach Vertragsabschluss. Sofortiger Versicherungsschutz."
  }, {
    icon: FileText,
    title: "Kündigungsfrist",
    description: "3 Monate zum Vertragsende. Im Schadensfall Sonderkündigungsrecht. Automatische Verlängerung."
  }, {
    icon: Award,
    title: "Marktführer",
    description: "Testsieger mit 5x 'Sehr Gut'. Umfangreiche Präventionsleistungen. Weltweiter Versicherungsschutz."
  }];
  const documents = [{
    title: "Allianz Cyber Produktinformation",
    type: "PDF, ca. 900 KB",
    url: "/documents/Kurzinfo_Allianz_CyberSchutz.pdf"
  }, {
    title: "Versicherungsbedingungen (AVB)",
    type: "PDF, ca. 1,4 MB",
    url: "/documents/Allianz_Cyberschutz_AVB.pdf"
  }];
  const caseStudies = [{
    title: "Ransomware-Angriff auf Hausarztpraxis",
    description: "Eine Hausarztpraxis wurde Opfer eines Ransomware-Angriffs. Alle Patientendaten waren verschlüsselt, der Praxisbetrieb komplett lahmgelegt.",
    outcome: "Allianz stellte binnen 4 Stunden ein Forensik-Team bereit, verhandelte mit den Erpressern und stellte die Systeme innerhalb von 2 Tagen wieder her. Gesamtkosten: 45.000 € – vollständig erstattet.",
    damage: "45.000 €"
  }, {
    title: "DSGVO-Verstoß durch Datenpanne",
    description: "In einer Facharztpraxis wurden versehentlich 1.200 sensible Patientendaten inklusive Diagnosen und Behandlungsverläufen an falsche Empfänger verschickt.",
    outcome: "Die Allianz übernahm umfassend die Anwaltskosten für DSGVO-Beratung, koordinierte Meldungen an Behörden und Patientenbenachrichtigungen sowie das verhängte Bußgeld von 12.000 €.",
    damage: "28.000 €"
  }, {
    title: "Phishing-Angriff auf MVZ",
    description: "Ein medizinisches Versorgungszentrum wurde durch einen Phishing-Angriff kompromittiert. Administrator-Login-Daten wurden abgegriffen.",
    outcome: "Die Allianz koordinierte sofort IT-Forensik zur Schadensfeststellung, umfassende Systemhärtung und verpflichtende Mitarbeiterschulungen. Finanzielle Schäden von 18.000 € wurden vollständig reguliert.",
    damage: "18.000 €"
  }, {
    title: "Serverausfall durch DDoS-Attacke",
    description: "Eine moderne Zahnarztpraxis wurde Ziel einer DDoS-Attacke. Die Online-Terminbuchung und das Praxisverwaltungssystem waren 3 Tage nicht erreichbar.",
    outcome: "Die Allianz organisierte binnen Stunden ein IT-Notfallteam, stellte professionelle Backup-Systeme bereit und übernahm vollständig den entstandenen Umsatzausfall sowie sämtliche IT-Wiederherstellungskosten von 22.000 €.",
    damage: "22.000 €"
  }];
  const coverageAreas = [{
    icon: Shield,
    title: "Eigenschäden",
    items: ["IT-Forensik und Schadensanalyse", "Datenwiederherstellung nach Cyberangriffen", "Betriebsunterbrechung und Mehrkosten", "Cyber-Erpressung (Ransomware)", "Kosten für Krisenmanagement"]
  }, {
    icon: AlertCircle,
    title: "Cyber-Haftpflicht",
    items: ["Datenschutzverletzungen (DSGVO)", "Datenrechtsverletzungen", "Cyber-Verstöße gegen Dritte", "Abwehr unberechtigter Ansprüche", "Bußgelder und Strafen"]
  }, {
    icon: Phone,
    title: "24/7 Assistance",
    items: ["Cyber-Notfall-Hotline rund um die Uhr", "Sofortige Expertenunterstützung", "Koordination aller Dienstleister", "PR- und Krisenkommunikation", "Psychologische Erstbetreuung"]
  }, {
    icon: CheckCircle2,
    title: "Prävention",
    items: ["IT-Sicherheitsschulungen", "Awareness-Trainings für Mitarbeiter", "Regelmäßige Sicherheits-Updates", "Cyber-Risiko-Bewertung", "Best-Practice-Empfehlungen"]
  }];
  const targetGroups = [{
    icon: Stethoscope,
    title: "Hausarztpraxen",
    description: "Einzelpraxen mit hoher Patientenfrequenz und sensiblen Gesundheitsdaten",
    benefits: ["Schutz vor Ransomware-Angriffen auf Praxissoftware", "DSGVO-Absicherung bei Patientendatenverarbeitung", "24/7-Notfall-Hotline für schnelle Hilfe", "Präventive Sicherheitsschulungen für Mitarbeiter"],
    recommendation: "Empfohlene Deckung: 500.000 - 1.000.000 €"
  }, {
    icon: Users,
    title: "Facharztpraxen",
    description: "Spezialisierte Praxen mit umfangreichen digitalen Diagnosesystemen",
    benefits: ["Schutz teurer digitaler Diagnose- und Bildgebungsgeräte", "Absicherung bei Ausfall bildgebender Verfahren", "DSGVO-Schutz bei Datenübermittlung an Dritte", "Betriebsunterbrechung bei Systemausfällen"],
    recommendation: "Empfohlene Deckung: 1.000.000 - 2.000.000 €"
  }, {
    icon: Building2,
    title: "MVZ & Praxisgemeinschaften",
    description: "Mehrere Standorte mit vernetzten IT-Systemen und Cloud-Anbindung",
    benefits: ["Alle Standorte unter einem Vertrag versichert", "Cloud- und Homeoffice-Arbeitsplätze mitversichert", "Höhere Deckungssummen für komplexe Schadensfälle", "Persönlicher Schadenmanager als Ansprechpartner"],
    recommendation: "Empfohlene Deckung: 2.000.000 - 3.000.000 €"
  }, {
    icon: Hospital,
    title: "Kliniken & Krankenhäuser",
    description: "Große medizinische Einrichtungen mit kritischer IT-Infrastruktur",
    benefits: ["Maximalschutz für lebenserhaltende IT-Systeme", "Mitversicherung von Tochtergesellschaften", "Deckung von Massendatenverlusten", "Spezialisiertes Krisenmanagement für Healthcare"],
    recommendation: "Empfohlene Deckung: 3.000.000 - 5.000.000 €"
  }];
  return (
    <>
      <Helmet>
        <title>Allianz Cyberversicherung für Ärzte & Praxen 2025 | Testsieger & Marktführer</title>
        <meta name="description" content="Allianz Cyberversicherung mit 5x 'Sehr Gut', 24/7-Hotline & umfassenden Präventionsleistungen. Testsieger für medizinische Einrichtungen. Jetzt beraten lassen!" />
        <meta name="keywords" content="Allianz Cyberversicherung, Cyber-Versicherung Ärzte, Allianz Praxis Cyberschutz, Testsieger Cyberversicherung" />
        <link rel="canonical" href="https://praxis-versichert.de/tarifwissen/allianz" />
      </Helmet>
      
      <StructuredData data={schemas} />
      
      <Navigation />
        
          <main className="flex-grow">
            {/* Hero Section */}
            <section className="relative pt-20 sm:pt-24 pb-12 sm:pb-16 lg:pb-20 overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]"></div>
            <div className="absolute top-20 right-10 w-72 h-72 bg-[#003781] rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-blob"></div>
            <div className="absolute bottom-20 left-10 w-72 h-72 bg-foreground rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-blob animation-delay-2000"></div>
            
            <TariffBreadcrumbs companyName="Allianz" />
            
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
              <div className="max-w-4xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 bg-card/80 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border border-border/50 mb-4 sm:mb-6">
                  <Award className="w-3 h-3 sm:w-4 sm:h-4" style={{
                    color: ALLIANZ_COLOR
                  }} />
                  <span className="text-[10px] sm:text-xs font-medium" style={{
                    color: ALLIANZ_COLOR
                  }}>
                    Testsieger – 5x Sehr Gut – Marktführer
                  </span>
                </div>
                
                <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-3 sm:mb-4 lg:mb-6 bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
                  Allianz Cyberversicherung für medizinische Einrichtungen
                </h1>
                
                <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto">
                  Umfassender 360°-Schutz vom Marktführer. Ausgezeichnet mit 5x "Sehr Gut" und 24/7-Notfall-Hotline. 
                  Inklusive Präventionsleistungen und weltweitem Versicherungsschutz.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12">
                  <Button asChild size="lg" className="w-full sm:w-auto text-sm sm:text-base group shadow-lg hover:shadow-xl transition-all duration-300" style={{
                    backgroundColor: ALLIANZ_COLOR,
                    color: '#FFFFFF'
                  }}>
                    <Link to="/termin-buchen">
                      Jetzt beraten lassen
                      <ChevronRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto text-sm sm:text-base border-2 text-white hover:text-white hover:bg-[#003781]/10" style={{
                    borderColor: ALLIANZ_COLOR,
                    boxShadow: '0 0 20px rgba(0, 55, 129, 0.4)'
                  }}>
                    <Link to="/tarifwissen">Alle Tarife vergleichen</Link>
                  </Button>
                </div>

                {/* Key Features */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
                  <div className="flex items-center gap-3 bg-card/50 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-border/50">
                    <Award className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{
                      color: ALLIANZ_COLOR
                    }} />
                    <div className="text-left">
                      <div className="font-semibold text-xs sm:text-sm">Testsieger</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground">5x Sehr Gut</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-card/50 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-border/50">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{
                      color: ALLIANZ_COLOR
                    }} />
                    <div className="text-left">
                      <div className="font-semibold text-xs sm:text-sm">24/7 Hotline</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground">Sofort erreichbar</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-card/50 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-border/50">
                    <Shield className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{
                      color: ALLIANZ_COLOR
                    }} />
                    <div className="text-left">
                      <div className="font-semibold text-xs sm:text-sm">Bis 5 Mio. €</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground">Deckungssumme</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Intro Alert */}
          <section className="py-8 sm:py-12">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <Alert className="max-w-4xl mx-auto border-l-4" style={{
                borderLeftColor: ALLIANZ_COLOR
              }}>
                <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5" style={{
                  color: ALLIANZ_COLOR
                }} />
                <AlertDescription className="text-sm sm:text-base ml-2">
                  <strong>Warum Allianz?</strong> Als Marktführer und Testsieger (5x "Sehr Gut") bietet die Allianz umfassenden Cyber-Schutz für medizinische Einrichtungen. 
                  Die 24/7-Notfall-Hotline, präventive Sicherheitsschulungen und weltweiter Versicherungsschutz machen die Allianz zur ersten Wahl für Arztpraxen, MVZ und Kliniken.
                </AlertDescription>
              </Alert>
            </div>
          </section>

          {/* Coverage Areas - 4er Grid */}
          <section className="py-16 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                  Umfassender 360°-Schutz
                </h2>
                <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
                  Die Allianz Cyberversicherung deckt alle Bereiche ab
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-7xl mx-auto">
                {coverageAreas.map((area, index) => {
                  const Icon = area.icon;
                  return <Card key={index} className="hover:shadow-lg transition-all duration-300 hover-scale">
                      <CardHeader className="text-center">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mb-4 mx-auto" style={{
                        backgroundColor: `${ALLIANZ_COLOR}15`
                      }}>
                          <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{
                          color: ALLIANZ_COLOR
                        }} />
                        </div>
                        <CardTitle className="text-lg sm:text-xl">{area.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {area.items.map((item, itemIndex) => <li key={itemIndex} className="flex items-start gap-3">
                              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" style={{
                            color: ALLIANZ_COLOR
                          }} />
                              <span className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                                {item}
                              </span>
                            </li>)}
                        </ul>
                      </CardContent>
                    </Card>;
                })}
              </div>
            </div>
          </section>

          {/* Echte Schadensbeispiele */}
          

          {/* Target Groups - 2x2 Grid mit Gradient-Überschrift */}
          <section className="py-16 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                  Für wen ist die{" "}
                  <span className="bg-gradient-to-r from-[#003781] via-[#0052b3] to-[#003781] bg-clip-text text-transparent">
                    Allianz Cyberversicherung
                  </span>
                  {" "}geeignet?
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
                {targetGroups.map((group, index) => {
                  const Icon = group.icon;
                  return <Card key={index} className="hover:shadow-lg transition-all duration-300 hover-scale">
                      <CardHeader>
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                          backgroundColor: `${ALLIANZ_COLOR}15`
                        }}>
                            <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{
                            color: ALLIANZ_COLOR
                          }} />
                          </div>
                          <div>
                            <CardTitle className="text-lg sm:text-xl mb-2">{group.title}</CardTitle>
                            <p className="text-xs sm:text-sm text-muted-foreground">{group.description}</p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3 mb-4">
                          {group.benefits.map((benefit, benefitIndex) => <li key={benefitIndex} className="flex items-start gap-3">
                              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" style={{
                            color: ALLIANZ_COLOR
                          }} />
                              <span className="text-xs sm:text-sm text-muted-foreground">{benefit}</span>
                            </li>)}
                        </ul>
                        <p className="text-xs sm:text-sm font-semibold" style={{
                        color: ALLIANZ_COLOR
                      }}>
                          {group.recommendation}
                        </p>
                      </CardContent>
                    </Card>;
                })}
              </div>
            </div>
          </section>

          {/* Timeline Section */}
          <TimelineSection companyName="Allianz" companyColor={ALLIANZ_COLOR} />

          {/* Versicherungskonditionen - 3x2 Grid */}
          <InsuranceConditions conditions={insuranceConditions} />

          {/* FAQ Section */}
          <FAQSection questions={faqs} companyName="Allianz" />

          {/* Document Downloads */}
          <DocumentDownloads documents={documents} companyName="Allianz" />

          {/* Three Steps Protection */}
          <ThreeStepsProtection companyName="Allianz" companyColor={ALLIANZ_COLOR} />

          {/* Expert Section */}
          <ExpertSection />

          {/* CTA Section */}
          <CTASection />
        </main>

        <Footer />
        <StickyCTA companyName="Allianz" companyColor={ALLIANZ_COLOR} />
    </>
  );
};

export default AllianzTarif;